import { UserNotificationItem, UserNotificationType } from 'src/app/Data/service/http-help.service';

const URL_ALIASES: Record<string, string> = {
  '/expenses': '/dashboard/expenses',
  '/dashboard/subscription': '/dashboard/invoice-subscriptions',
};

const TASK_NOTIFICATION_TYPES: UserNotificationType[] = [
  'new_task',
  'new_message',
  'file',
  'mention',
  'merchant_reply',
  'task_closed',
  'task_reopened',
  'postponement_expired',
  'task_assigned',
  'participant_added',
];

/** Whether the notification had an explicit link from the backend (not inferred). */
export function notificationHasDirectLink(item: UserNotificationItem): boolean {
  return !!(item.url?.trim() || item.task_id);
}

/** Normalize notification URLs before router navigation. */
export function resolveNotificationNavigationUrl(
  item: UserNotificationItem,
  userType: number | null,
): string {
  if (item.url?.trim()) {
    const parsedTask = parseTaskIdFromUrl(item.url, userType);
    if (parsedTask) {
      return parsedTask;
    }
    const normalized = normalizeNotificationPath(item.url);
    if (normalized) {
      return normalized;
    }
  }

  const taskId = item.task_id;
  if (taskId) {
    const base = taskBasePath(taskId, userType);
    if (item.message_id) {
      return `${base}?messageId=${item.message_id}`;
    }
    return base;
  }

  return defaultRouteForNotificationType(item.notification_type, userType);
}

export function normalizeNotificationPath(url: string): string | null {
  if (!url?.trim()) {
    return null;
  }

  let path = url.trim();

  const hashIdx = path.indexOf('#/');
  if (hashIdx >= 0) {
    path = path.slice(hashIdx + 1);
  } else if (path.startsWith('http://') || path.startsWith('https://')) {
    try {
      const parsed = new URL(path);
      path = parsed.pathname || '/dashboard';
    } catch {
      return null;
    }
  }

  if (!path.startsWith('/')) {
    path = `/${path}`;
  }

  if (URL_ALIASES[path]) {
    path = URL_ALIASES[path];
  }

  return path;
}

export function readNotificationUserType(): number | null {
  const raw = localStorage.getItem('user_type');
  if (!raw) {
    return null;
  }
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

function defaultRouteForNotificationType(
  type: UserNotificationType | null | undefined,
  userType: number | null,
): string {
  const isOffice = userType === 300 || userType === 301;

  switch (type) {
    case 'expense_analysis_complete':
      return '/dashboard/expenses';
    case 'subscription_renewal_failed':
    case 'subscription_payment':
      return '/dashboard/invoice-subscriptions';
    case 'office_permission':
      return isOffice
        ? '/dashboard/notification-details?tab=requests'
        : '/dashboard/accountants';
    case 'accountant_access_granted':
      return '/dashboard/office-companies';
    case 'closed_month':
      return '/dashboard/accountants';
    case 'custom_payment':
      return '/dashboard';
    case 'broadcast':
    case 'other':
      return '/dashboard/notifications';
    default:
      if (type && TASK_NOTIFICATION_TYPES.includes(type)) {
        return '/dashboard/notifications';
      }
      return '/dashboard/notifications';
  }
}

function taskBasePath(taskId: number | string, userType: number | null): string {
  const isAdmin = userType === 200 || userType === 201;
  const isOffice = userType === 300 || userType === 301;
  if (isAdmin) {
    return `/dashboard/tasks/admin/${taskId}`;
  }
  if (isOffice) {
    return `/dashboard/tasks/office/${taskId}`;
  }
  return `/dashboard/tasks/merchant/${taskId}`;
}

function parseTaskIdFromUrl(url: string, userType: number | null): string | null {
  try {
    const parsed = new URL(url, window.location.origin);
    const taskId = parsed.searchParams.get('taskId');
    if (!taskId) {
      return null;
    }
    const messageId = parsed.searchParams.get('messageId');
    const base = taskBasePath(taskId, userType);
    return messageId ? `${base}?messageId=${messageId}` : base;
  } catch {
    const match = url.match(/[?&]taskId=(\d+)/);
    if (!match) {
      return null;
    }
    const base = taskBasePath(match[1], userType);
    const messageMatch = url.match(/[?&]messageId=(\d+)/);
    return messageMatch ? `${base}?messageId=${messageMatch[1]}` : base;
  }
}
